import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LinearProgress, CircularProgress } from '@mui/material';
import { motion } from "motion/react";
import ConfettiExplosion from 'react-confetti-explosion';
import { createCompany, verifyCompany } from '../SignupVerification/verify';
import { Helmet } from 'react-helmet';
import { storefrontClient } from '../ShopifyApi/client';
import { CUSTOMER_CREATE } from '../ShopifyApi/queries/customer';

const questions = [
  {
    question: 'About You',
    type: 'about',
    questions: [
      'First Name',
      'Last Name',
    ],
  },
  {
    question: 'Company Details',
    type: 'address',
    questions: [
      'Name',
      'Address',
      'City',
      'State (Province)',
      'Zip code',
      'Phone number',
      'Email',
      'Tax ID',
      'Company Website (optional)'
    ],
  },
  {
    question: 'What is your preferred wholesaler?',
    type: 'select',
    options: ['Ferguson', 'Baker Distributing', 'Gemaire', 'Johnstone Supply', 'Daikin', 'Other'],
  },
  {
    question: 'How many trucks do you operate?',
    type: 'select',
    options: ['1-5', '5-10', '10-50', '50+', 'Other'],
  },
];

export default function Quiz() {
  const [answers, setAnswers] = useState({});
  const [errorMessage, setMessage] = useState('');
  const [verResult, setVerResult] = useState("Congrats your company is Verified");
  const [verifying, setVerifying] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [verStatus, setVerStatus] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const errorRef = useRef(null);

  const requiredFields = [
    'About You - First Name',
    'About You - Last Name',
    'Company Details - Name',
    'Company Details - Address',
    'Company Details - City',
    'Company Details - State (Province)',
    'Company Details - Zip code',
    'Company Details - Phone number',
    'Company Details - Email',
    'Company Details - Tax ID'
  ];

  const isFieldEmpty = (field) => {
    return !answers[field] || answers[field].trim() === '';
  };

  const isRequiredField = (field) => {
    return requiredFields.includes(field);
  };

  const scrollToError = () => {
    errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  useEffect(() => {
    if (errorMessage) {
      scrollToError();
    }
  }, [errorMessage]);

  const handleFinish = () => {
    setShowValidation(true);
    // Check if all required answers are completed
    const allFieldsFilled = requiredFields.every(field => {
      const hasField = Boolean(answers[field]);
      return hasField;
    });

    if (allFieldsFilled) {
      setVerifying(true);

      setTimeout(() => {
        // Commenting out verifyCompany check
        // verifyCompany(answers['Company Details - Name'], answers['Company Details - Company Website'], answers['Company Details - Address'])
        // .then((data) => {
        //   if (data) {
            // create company in shopify, check if it returns valid before verifying
            createCompany({
              name: answers['Company Details - Name'],
              externalId: Math.random().toString(36).slice(2),
              metafields: {
                wholesaler: answers['What is your preferred wholesaler?'],
                trucks: answers['How many trucks do you operate?'],
              },
              location: {
                name: answers['Company Details - Name'],
                shippingAddress: {
                  firstName: answers['About You - First Name'],
                  lastName: answers['About You - Last Name'],
                  address1: answers['Company Details - Address'],
                  city: answers['Company Details - City'],
                  province: answers['Company Details - State (Province)'],
                  zip: answers['Company Details - Zip code'],
                },
                taxRegistrationId: answers['Company Details - Tax ID']
              },
              contact: {
                email: answers['Company Details - Email'],
                firstName: answers['About You - First Name'],
                lastName: answers['About You - Last Name'],
              },
            })
            .then(response => {
              //console.log("Customer created successfully:", response);
              // Only show success after confirming account creation
              
              // Send data to Zapier webhook
              fetch('https://hooks.zapier.com/hooks/catch/19433399/2awjrm8/', {
                method: 'POST',
                body: JSON.stringify({
                  companyName: answers['Company Details - Name'],
                  firstName: answers['About You - First Name'],
                  lastName: answers['About You - Last Name'],
                  email: answers['Company Details - Email'],
                  phone: answers['Company Details - Phone number'],
                  address: answers['Company Details - Address'],
                  city: answers['Company Details - City'],
                  state: answers['Company Details - State (Province)'],
                  zipCode: answers['Company Details - Zip code'],
                  taxId: answers['Company Details - Tax ID'],
                  website: answers['Company Details - Company Website (optional)'],
                  wholesaler: answers['What is your preferred wholesaler?'],
                  trucks: answers['How many trucks do you operate?']
                })
              });
              
              setTimeout(() => {
                setVerResult('Verified');
                setIsExploding(true);
                setVerStatus(true);
                setTimeout(() => {
                  setVerifying(false);
                }, 2000);
              }, 1000);
            })
            .catch(error => {
              //console.error("Error creating customer:", error);
              setVerifying(false);
              if (error) {
                setMessage(error.message);
                setVerResult('Verification Failed');
              } else if (error.message === 'Firebase: Error (auth/email-already-in-use).') {
                setMessage('Email already in use. Please try a different email.');
                setVerResult('Email already in use');
              } else {
                setMessage('Verification Failed. Please try again.');
                setVerResult('Verification Failed');
              }
              setIsExploding(false);
              // Add delay before showing error state
              setTimeout(() => {
                setVerStatus(false);
              }, 2500);
            });
          // } else {
          //   setVerResult('Verification Failed');
          //   setIsExploding(false);
          //   // Add delay before showing error state
          //   setMessage('Error: Please try again');
          //   setTimeout(() => {
          //     setVerStatus(false);
          //   }, 2500);
          // }
        // })
        // .catch((error) => {
        //   //onsole.error('Verification error:', error);
        //   setVerifying(false);
        //   setIsExploding(false);
        //   setVerStatus(false);
        //   setMessage('Unable to verify company. Please check your information and try again.');
        //   setVerResult('Verification Failed: Please check your company information');
        // });
      }, 2500)
    } else {
      setMessage('Please fill in all required fields');
    }
  };

  return (
    <div className="bg-[#202020] min-h-screen">
      <Helmet>
        <title>iFLO Pro | Signup </title>
        <meta name="description" content="Sign up for iFLO Pro to access exclusive features and benefits." />
        <meta name="keywords" content="iFLO Pro, signup, HVAC, company details, verification" />
        <meta property="og:title" content="iFLO Pro | Signup" />
        <meta property="og:description" content="Join iFLO Pro today and unlock exclusive benefits for your company." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="w-full">
        <LinearProgress
          sx={{
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'gray',
            },
            '& .MuiLinearProgress-bar1Buffer': {
              backgroundColor: 'rgba(243, 246, 59, 0.5)',
            },
          }}
          variant="buffer"
          value={100}
          valueBuffer={100}
        />
      </div>

      <div className="flex flex-col items-center justify-center min-h-[50em] p-4">
        {!verifying && !verStatus ? (
          <div className="w-full max-w-3xl p-6 rounded-lg animate-fade-in">
            <h1 className="text-2xl font-bold text-white mb-8">Company Registration</h1>
            
            {questions.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-12">
                <h2 className="text-xl font-semibold mb-4 text-white">{section.question}</h2>
                <div className="flex flex-col gap-3">
                  {section.type === 'select' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.options.map((option, index) => (
                        <button
                          key={index}
                          onClick={() =>
                            setAnswers((prevAnswers) => ({
                              ...prevAnswers,
                              [section.question]: option,
                            }))
                          }
                          className="py-2 px-4 text-white rounded-lg hover:bg-blue-600 focus:outline-none transition"
                          style={{
                            backgroundColor:
                              answers[section.question] === option
                                ? 'rgba(243, 246, 59, 0.5)'
                                : 'transparent',
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}

                  {section.type === 'select' &&
                    answers[section.question] === 'Other' && (
                      <input
                        type="text"
                        placeholder="Please enter your answer"
                        className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none mt-2"
                        value={
                          answers[`${section.question} - Other`] || ''
                        }
                        onChange={(e) =>
                          setAnswers((prevAnswers) => ({
                            ...prevAnswers,
                            [`${section.question} - Other`]: e.target.value,
                          }))
                        }
                      />
                    )}

                  {(section.type === 'address' || section.type === 'about') && (
                    <div className="grid md:grid-cols-2 gap-4">
                      {section.questions.map((question, index) => {
                        const fieldName = `${section.question} - ${question}`;
                        const isEmpty = showValidation && isFieldEmpty(fieldName);
                        const isRequired = isRequiredField(fieldName);
                        return (
                          <input
                            key={index}
                            type={index === 2 ? 'tel' : index === 4 ? 'email' : question === 'Tax ID' ? 'number' : 'text'}
                            pattern={question === 'Tax ID' ? '[0-9]*' : undefined}
                            inputMode={question === 'Tax ID' ? 'numeric' : undefined}
                            placeholder={question}
                            className={`px-4 py-2 rounded-lg border focus:outline-none ${
                              isEmpty && isRequired
                                ? 'border-red-500 bg-red-50' 
                                : 'border-gray-300'
                            }`}
                            value={answers[fieldName] || ''}
                            onChange={(e) =>
                              setAnswers((prevAnswers) => ({
                                ...prevAnswers,
                                [fieldName]: e.target.value,
                              }))
                            }
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div className="flex justify-center mt-8">
              <button
                onClick={handleFinish}
                className="py-3 px-8 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none transition text-lg font-semibold"
              >
                Complete Registration
              </button>
            </div>
            <p ref={errorRef} className="text-center mt-4 text-red-500">{errorMessage}</p>
          </div>
        ) : (
          <div className="w-full max-w-md mx-auto items-center justify-center p-6 rounded-lg animate-fade-in">
            {verifying ? (
              <div className="flex flex-col items-center justify-center">
                <p className="text-lg text-center font-semibold mb-4 text-white">We are verifying your info, Hold Tight!</p>
                <CircularProgress size={40} color="primary" />
              </div>  
            ) : (
              <>
                {verStatus && (
                  <>
                    <motion.div
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      exit={{ y: 60, opacity: 0, transition: { duration: 1.5 } }}
                      className="flex flex-col items-center justify-center w-full"
                    >
                      {isExploding && (
                        <ConfettiExplosion />
                      )}
                      <p className="text-2xl text-center font-semibold mb-4 text-white">{verResult}</p>
                    </motion.div>
        
                    <motion.p
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1.5 }}
                      exit={{ y: 60, opacity: 0, transition: { duration: 0.5, delay: 2.5 } }}
                      className="text-lg text-center font-semibold mb-4 text-white"
                    >
                      Thanks for signing up!
                    </motion.p>
        
                    <motion.p
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 3.5 }}
                      exit={{ y: 60, opacity: 0, transition: { duration: 0.5, delay: 4.5 } }}
                      className="text-lg text-center font-semibold mb-4 text-white"
                    >
                      Check your email to login!
                    </motion.p>
                  </>
                ) || (
                  <>
                    <motion.p
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      exit={{ y: 60, opacity: 0, transition: { duration: 0.5, delay: 1.5 } }}
                      className="text-lg text-center font-semibold mb-4 text-white"
                    >
                      {verResult}
                    </motion.p>

                    <motion.button
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 1.5 }}
                      exit={{ y: 60, opacity: 0, transition: { duration: 0.5, delay: 2.5 } }}
                      onClick={() => window.location.reload()}
                      className="mx-auto mt-4 flex py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none transition"
                    >
                      Try Again
                    </motion.button>

                    <motion.p
                      initial={{ y: 60, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.5, delay: 2.5 }}
                      exit={{ y: 60, opacity: 0, transition: { duration: 0.5, delay: 3.5 } }}
                      className="text-lg text-center font-semibold mb-4 mt-4 text-white"
                    >
                      Need help? <a href="/contact-us" className="text-white underline">Click Here to Contact Us</a>
                    </motion.p>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


